import { needsSourceReplacement, sourceCandidateRisk, sourceTextRisk } from '../lib/sourceEditPolicy.js';
import { useEffect, useRef, useState } from 'react';
import { Center, Loader, Text } from '@mantine/core';
import { getPdfJsDocument } from '../lib/pdfPreview.js';
import { normalizeAngle } from '../lib/geometry.js'; 
import { clamp, positionWithinPage, resizeRect, visualBounds } from '../lib/overlayGeometry.js';
import { cssFontStack } from '../lib/fontCatalog.js';
import { textSelectionRuns, groupTextLines } from '../lib/nativeSelection.js';
import { imageSelectionRegions } from '../lib/sourceObjects.js';
import { needsPreviewSourceRepair, repairRegion } from '../lib/backgroundRepair.js';
import { transparentizeTextPixels } from '../lib/textCutout.js';
import { inferTextColors } from '../lib/nativeAppearance.js';
import { extractPaintedPdfImage } from '../lib/extractPdfImage.js';
import { chooseImagePickResult } from '../lib/imagePickPolicy.js';
import { proposeCompositeGroup } from '../lib/compositeGrouping.js';
import { isolateFlatGroupPixels } from '../lib/compositeSnapshot.js';
import { isInPlaceCover, isCompositeGroup } from '../lib/sourceEditPolicy.js';
import { OPS } from 'pdfjs-dist';

/** PDF.js paints the unchanged PDF; an independent, editable DOM overlay sits above it.
 * All overlay coordinates use visual PDF points (not pixels) so zoom is lossless. */
export function PdfCanvas({ descriptor, sources, cache, thumbnail = false, zoom = 1,
  onPointClick, selectedOverlayId, textDraft, onSelectOverlay, onOverlayChange, onOverlayDoubleClick,
  editMode, onNativeTextPick, onNativeImagePick,
  unifiedNative = null,
  className = '' }) {
  const canvasRef = useRef(null);
  const sourceSnapshotRef = useRef(null);
  const gestureRef = useRef(null);
  const lastGestureEndRef = useRef(0);
  const draftRef = useRef(null);
  const nativeDragRef = useRef(null);
  const [sourceImages,setSourceImages] = useState([]);
  const [sourceTexts,setSourceTexts] = useState([]);
  const [draft, setDraft] = useState(null);
  const [surface, setSurface] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const kind = descriptor?.kind;
  const sourceId = descriptor?.sourceId;
  const pageIndex = descriptor?.pageIndex;
  const rotation = descriptor?.rotation;
  const width = descriptor?.width;
  const height = descriptor?.height;
  const baseRotation = descriptor?.baseRotation;
  const bounds = descriptor ? visualBounds(descriptor) : { width: 595, height: 842 };
  const scale = thumbnail ? 168 / bounds.width : zoom;
  // Visible dimensions for the underlying canvas may differ very slightly from
  // the pdf-lib crop box due to a source PDF's nonstandard page boundaries.
  const actualScale = surface ? surface.width / bounds.width : scale;

  useEffect(() => {
    let cancelled = false;
    let renderTask;
    setError('');
    setLoading(true);
    setSurface(null);
    setDraft(null);
    setSourceTexts([]);
    setSourceImages([]);
    sourceSnapshotRef.current=null;
    draftRef.current = null;

    async function render() {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const page = kind === 'pdf'
        ? await (await getPdfJsDocument(sourceId, sources, cache)).getPage(pageIndex + 1)
        : null;
      if (cancelled || !canvasRef.current) return;
      const angle = normalizeAngle((page?.rotate ?? 0) + rotation);
      const firstViewport = page?.getViewport({ scale: 1, rotation: angle });
      const viewScale = thumbnail ? 168 / (firstViewport?.width ?? bounds.width) : zoom;
      const viewport = page?.getViewport({ scale: viewScale, rotation: angle });
      const viewWidth = viewport?.width ?? bounds.width * viewScale;
      const viewHeight = viewport?.height ?? bounds.height * viewScale;
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.round(viewWidth * ratio));
      canvas.height = Math.max(1, Math.round(viewHeight * ratio));
      canvas.style.width = `${viewWidth}px`;
      canvas.style.height = `${viewHeight}px`;
      const ctx = canvas.getContext('2d', {willReadFrequently: true});
      if (!ctx) throw new Error('Canvas çizim alanı açılamadı.');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      if (page) {
        renderTask = page.render({ canvas, canvasContext: ctx, viewport,
          transform: [ratio, 0, 0, ratio, 0, 0] });
        await renderTask.promise;
      }
      if (!cancelled) {
        const snapshot=document.createElement('canvas');
        snapshot.width=canvas.width; snapshot.height=canvas.height;
        snapshot.getContext('2d').drawImage(canvas,0,0);
        sourceSnapshotRef.current=snapshot;
        setSurface({ width: viewWidth, height: viewHeight }); setLoading(false);
      }
    }
    render().catch((err) => {
      if (!cancelled && err?.name !== 'RenderingCancelledException') {
        setLoading(false); setError('Bu sayfa görüntülenemedi.'); console.error('PDF render:', err);
      }
    });
    return () => { cancelled = true; if (renderTask) renderTask.cancel(); };
  }, [kind, sourceId, pageIndex, rotation, width, height, baseRotation, zoom, thumbnail, sources, cache]);

  // One Adobe-style editing mode: text and painted PDF images are discovered
  // without asking the user to draw an image rectangle.
  useEffect(() => {
    let cancel=false;
    setSourceTexts([]);setSourceImages([]);
    if (thumbnail || !['pdf','images'].includes(editMode) || descriptor?.kind !== 'pdf' || !surface) return;
    (async () => {
      const pdf=await getPdfJsDocument(sourceId,sources,cache);
      const page=await pdf.getPage(pageIndex+1);
      const viewport=page.getViewport({scale:1,rotation:normalizeAngle(page.rotate+rotation)});
      const [content,operators]=await Promise.all([page.getTextContent(),page.getOperatorList()]);
      if(!cancel) {
        const texts=groupTextLines(textSelectionRuns(content,viewport,bounds));
        const images=imageSelectionRegions(operators,viewport,bounds,OPS);
        setSourceImages(images.map(img=>({...img,sourceRisk:sourceCandidateRisk(img,images,texts)})));
        setSourceTexts(texts.map(run=>({...run,sourceRisk:sourceTextRisk(run,images)})));
      }
    })().catch(e=>console.error('PDF içerik analizi:',e));
    return () => {cancel=true;};
  },[thumbnail,editMode,sourceId,pageIndex,rotation,surface?.width,surface?.height,cache,sources]);

  // Restore the unmodified PDF paint and repair areas of converted objects.
  // The same repair algorithm is used in the downloaded PDF.
  useEffect(() => {
    const canvas=canvasRef.current, snapshot=sourceSnapshotRef.current;
    if(!canvas || !snapshot || !surface) return;
    const ctx=canvas.getContext('2d');
    ctx.clearRect(0,0,canvas.width,canvas.height);
    ctx.drawImage(snapshot,0,0);
    // Only original PDF objects require removal of their previous paint.
    // Newly added text has no originalRegion. Changing its text updates the DOM
    // overlay only; its edit must never run through repairRegion.
    const sourceItems = (descriptor?.overlays || []).filter(item =>
      needsPreviewSourceRepair(item, textDraft, needsSourceReplacement(item)) &&
      (!item.sourceRisk || isCompositeGroup(item))
    );
    const sx = canvas.width / bounds.width;
    const sy = canvas.height / bounds.height;
    for (const item of sourceItems) {
      repairRegion(ctx, item.originalRegion, sx, sy);
    }
  },[surface,descriptor?.overlays,bounds.width,bounds.height,textDraft?.id,textDraft?.text]);

  async function snapshotRegion(rect, isText = false, group = false) {
    // A moved source glyph/image should not be limited by viewer zoom.
    // Render a separate high-resolution source snapshot on demand, capped to
    // avoid enormous canvas allocations with scanned/A0-sized PDF pages.
    let source=sourceSnapshotRef.current;
    if(kind==='pdf') {
      try {
        const pdf=await getPdfJsDocument(sourceId,sources,cache);
        const page=await pdf.getPage(pageIndex+1);
        const maxPixels=16_000_000;
        const highScale=Math.min(3,Math.sqrt(maxPixels/(bounds.width*bounds.height)));
        const viewport=page.getViewport({scale:highScale,
          rotation:normalizeAngle(page.rotate+rotation)});
        const high=document.createElement('canvas');
        high.width=Math.ceil(viewport.width);high.height=Math.ceil(viewport.height);
        if(high.width*high.height<=maxPixels*1.03){
          const context=high.getContext('2d',{willReadFrequently:true});
          context.fillStyle='#fff';context.fillRect(0,0,high.width,high.height);
          await page.render({canvas:high,canvasContext:context,viewport}).promise;
          source=high;
        }
      } catch (error) { console.warn('Yüksek çözünürlüklü kaynak örnekleme başarısız.',error); }
    }
    if(!source) return null;
    const sx=source.width/bounds.width,sy=source.height/bounds.height;
    const clip=document.createElement('canvas');
    clip.width=Math.max(1,Math.round(rect.width*sx));
    clip.height=Math.max(1,Math.round(rect.height*sy));
    const cx=clip.getContext('2d');
    cx.drawImage(source,rect.x*sx,rect.y*sy,
      rect.width*sx,rect.height*sy,0,0,clip.width,clip.height);
    let appearance = null;
    if(isText) {
      const data=cx.getImageData(0,0,clip.width,clip.height);
      appearance=inferTextColors(data.data,clip.width,clip.height);
      if(transparentizeTextPixels(data.data,clip.width,clip.height)) cx.putImageData(data,0,0);
    }
    let groupBackgroundFlat=null;
    if (group) {
      const data=cx.getImageData(0,0,clip.width,clip.height);
      const isolated=isolateFlatGroupPixels(data.data,clip.width,clip.height);
      groupBackgroundFlat=isolated.flat;
      if(isolated.flat) {
        data.data.set(isolated.data);
        cx.putImageData(data,0,0);
      }
    }
    const blob=await new Promise(resolve=>clip.toBlob(resolve,'image/png'));
    return {blob, appearance, groupBackgroundFlat};
  }

  async function pickDetectedText(e,run) {
    e.stopPropagation();
    if(descriptor?.overlays?.some(o=>o.sourceObjectKey===run.id)) return;
    const result=await snapshotRegion(run,true);
    onNativeTextPick?.(run,result?.blob,result?.appearance);
  }
  async function pickDetectedImage(e,rect) {
    e.stopPropagation();
    if(descriptor?.overlays?.some(o=>o.sourceObjectKey===rect.id)) return;
    let blob=null;
    try {
      const pdf=await getPdfJsDocument(sourceId,sources,cache);
      const page=await pdf.getPage(pageIndex+1);
      blob=await extractPaintedPdfImage(page,rect.operatorIndex,OPS);
    } catch(error) { console.warn('PDF kaynak görüntüsü çıkarılamadı:',error); }
    // Screenshot cropping is an explicit visual group option, NEVER silently
    // substituted for native PDF image extraction. It includes anything inside
    // the whole region (text, vector art, transparency, etc.).
    let screenshot=null, groupBlob=null, groupBackgroundFlat=null;
    let groupRegion={x:rect.x,y:rect.y,width:rect.width,height:rect.height};
    if(rect.sourceRisk) groupRegion=proposeCompositeGroup(rect,sourceImages,sourceTexts,bounds);
    try {
      if(!blob || rect.sourceRisk) screenshot=(await snapshotRegion(rect,false))?.blob || null;
      if(rect.sourceRisk) {
        const group=(await snapshotRegion(groupRegion,false,true));
        groupBlob=group?.blob || null;
        groupBackgroundFlat=group?.groupBackgroundFlat ?? null;
      }
    } catch(error) { console.warn('Bileşik görsel önizlemesi oluşturulamadı:',error); }
    onNativeImagePick?.(rect, {...chooseImagePickResult(rect,blob,screenshot),groupRegion,groupBlob,groupBackgroundFlat});
  }

  function pointOfPointer(e) {
    const r=e.currentTarget.getBoundingClientRect();
    return {x:clamp((e.clientX-r.left)/actualScale,0,bounds.width),
      y:clamp((e.clientY-r.top)/actualScale,0,bounds.height)};
  }
  function clickSurface(event) {
    // Pointer-capture can retarget the final click to the overlay layer.
    // Do not deselect a just-dragged text/image object on that click.
    if (editMode) return;
    if (Date.now() - lastGestureEndRef.current < 250) return;
    if (!onPointClick || loading || error) {
      if (!event.target.closest?.('.pdf-overlay-object')) onSelectOverlay?.(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const x = (event.clientX - rect.left) / actualScale;
    const y = (event.clientY - rect.top) / actualScale;
    onPointClick({ x: clamp(x, 0, bounds.width), y: clamp(y, 0, bounds.height) });
  }

  // The stable overlay layer captures the pointer rather than an individual
  // text/image node. Text contents and resize handles therefore behave just
  // like image contents, even if React re-renders the selected object mid-drag.
  function startGesture(event) {
    if (thumbnail || !onOverlayChange || onPointClick || event.button !== 0) return;
    const element = event.target.closest?.('[data-annotation-id]');
    if (!element || !event.currentTarget.contains(element)) return;
    const item = descriptor?.overlays?.find((candidate) => candidate.id === element.dataset.annotationId);
    if (!item || item.hidden || isInPlaceCover(item)) return;
    event.preventDefault();
    event.stopPropagation();
    onSelectOverlay?.(item.id);
    const handle = event.target.closest('[data-resize]')?.dataset.resize ?? null;
    const start = { x: item.x, y: item.y, width: item.width, height: item.height };
    gestureRef.current = {
      id: item.id, pointerId: event.pointerId,
      startClientX: event.clientX, startClientY: event.clientY,
      start, handle, lockAspect: item.type === 'image',
    };
    // A stable element remains mounted while drag positions update.
    try { event.currentTarget.setPointerCapture(event.pointerId); }
    catch { /* Some synthetic pointers do not support capture. */ }
  }

  function moveGesture(event) {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    event.preventDefault();
    event.stopPropagation();
    const deltaX = (event.clientX - gesture.startClientX) / actualScale;
    const deltaY = (event.clientY - gesture.startClientY) / actualScale;
    const next = gesture.handle
      ? resizeRect(gesture.start, gesture.handle, deltaX, deltaY, bounds, gesture.lockAspect && !event.shiftKey)
      : positionWithinPage({ ...gesture.start,
          x: gesture.start.x + deltaX, y: gesture.start.y + deltaY }, bounds);
    draftRef.current = { id: gesture.id, rect: next };
    setDraft(draftRef.current);
  }

  function endGesture(event) {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    event.preventDefault();
    event.stopPropagation();
    gestureRef.current = null;
    lastGestureEndRef.current = Date.now();
    try {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    } catch { /* Pointer was already released. */ }
    // A full drag is committed once, giving undo a single clear step.
    const result = draftRef.current;
    if (event.type !== 'pointercancel' && result?.id === gesture.id) {
      onOverlayChange?.(gesture.id, result.rect);
    }
    draftRef.current = null;
    setDraft(null);
  }

  function onOverlayKeyDown(event, item) {
    if (thumbnail || !onOverlayChange || onPointClick) return;
    const offsets = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    const shift = offsets[event.key];
    if (!shift || event.altKey || event.ctrlKey || event.metaKey) return;
    event.preventDefault();
    event.stopPropagation();
    onSelectOverlay?.(item.id);
    const step = event.shiftKey ? 10 : 1;
    const next = positionWithinPage({ ...item, x: item.x + shift[0] * step, y: item.y + shift[1] * step }, bounds);
    onOverlayChange(item.id, { x: next.x, y: next.y });
  }

  const objects = descriptor?.overlays || [];
  const handledKeys = new Set(objects.map(o=>o.sourceObjectKey).filter(Boolean));
  return (
    <div className={`pdf-canvas-shell ${onPointClick ? 'is-placement' : ''} ${className}`}
      style={surface ? { width: surface.width, height: surface.height } : undefined}
      onClick={clickSurface}>
      <canvas ref={canvasRef} aria-label="PDF sayfa önizlemesi" />
      {surface && <div className={`pdf-overlay-layer ${['pdf','images','native','native-images'].includes(editMode)?'pdf-edit-objects':''}`} style={{ pointerEvents: onPointClick || thumbnail || ['pdf','images','native','native-images'].includes(editMode) ? 'none' : 'auto' }}
        onPointerDown={startGesture} onPointerMove={moveGesture}
        onPointerUp={endGesture} onPointerCancel={endGesture}>
        {objects.map((item) => {
          if (item.hidden) return null;
          const position = draft?.id === item.id ? { ...item, ...draft.rect } : item;
          const selected = !thumbnail && selectedOverlayId === item.id;
          return <div key={item.id} data-annotation-id={item.id}
            className={`pdf-overlay-object ${selected ? 'pdf-overlay-selected' : ''} ${thumbnail ? 'pdf-overlay-thumbnail' : ''} pdf-overlay-${item.type}`}
            style={{
              left: position.x * actualScale, top: position.y * actualScale,
              width: position.width * actualScale, height: position.height * actualScale,
              cursor: isInPlaceCover(item) ? 'default' : selected ? 'move' : 'pointer',
            }}
            tabIndex={thumbnail ? undefined : 0}
            role={thumbnail ? undefined : 'button'}
            aria-label={thumbnail ? undefined : `${item.type === 'text' ? 'Metin' : 'Görsel'} nesnesi: taşı, köşelerden boyutlandır`}
            onKeyDown={(e) => onOverlayKeyDown(e, item)}
            onClick={(e) => { if (!thumbnail) { e.stopPropagation(); if (!onPointClick) onSelectOverlay?.(item.id); } }}
            onDoubleClick={(e) => { e.stopPropagation(); if (!thumbnail) onOverlayDoubleClick?.(item.id); }}
            title={thumbnail ? undefined : 'Sürükle • Çift tıkla: düzenle'}>
            {item.originalRegion && !needsSourceReplacement(item) && !(textDraft?.id === item.id && textDraft.text !== item.text) ? null : item.type === 'image' || (item.type === 'text' && item.originalPreviewUrl && !item.editDirty && textDraft?.id !== item.id)
              ? <img draggable={false} alt={item.type==='text'?'PDF kaynak metninin özgün görünümü':'PDF üzerine eklenen görsel'} src={item.type==='text'?item.originalPreviewUrl:item.previewUrl}
                  style={{ opacity: item.opacity ?? 1, backgroundColor: isInPlaceCover(item) ? item.coverColor || '#ffffff' : 'transparent' }} />
              : <div className="pdf-overlay-text" style={{
                  fontSize: item.fontSize * actualScale,
                  fontFamily: cssFontStack(item.fontFamily),
                  fontWeight:item.bold ? 700 : 400, fontStyle:item.italic?'italic':'normal',
                  textDecoration:[item.underline?'underline':'',item.strikeThrough?'line-through':''].filter(Boolean).join(' ')||'none',
                  textAlign:item.textAlign||'left', letterSpacing: `${item.letterSpacing||0}px`,
                  lineHeight:item.lineSpacing||1.25, color: item.color, backgroundColor:item.backgroundColor||'transparent',
                }}>{textDraft?.id === item.id ? textDraft.text : item.text}</div>}
            {selected && !onPointClick && <span className="pdf-object-drag-label" aria-hidden="true">⠿ Taşı</span>}
            {selected && !onPointClick && !isInPlaceCover(item) && ['nw', 'ne', 'sw', 'se'].map((corner) =>
              <span className={`pdf-resize-handle pdf-resize-${corner}`} data-resize={corner} key={corner} />)}
          </div>;
        })}
      </div>}
      {surface && ['pdf','images'].includes(editMode) && !thumbnail && <div className="pdf-native-hit-layer">
        {[...sourceImages].reverse().filter(o=>!handledKeys.has(o.id)).map(box=><button key={box.id} type="button"
          className="pdf-native-image-hit" title={box.sourceRisk || "PDF görselini seç, taşı veya değiştir"}
          style={{left:box.x*actualScale,top:box.y*actualScale,width:box.width*actualScale,height:box.height*actualScale}}
          onClick={e=>pickDetectedImage(e,box)} />)}
        {editMode==='pdf' && sourceTexts.filter(o=>!handledKeys.has(o.id)).map(run=><button key={run.id} type="button"
          className="pdf-native-text-hit" title={run.sourceRisk || `PDF metnini düzenle: ${run.text}`}
          style={{left:run.x*actualScale,top:run.y*actualScale,width:run.width*actualScale,height:run.height*actualScale}}
          onClick={e=>pickDetectedText(e,run)} />)}
        {!sourceTexts.length && !sourceImages.length && <span className="pdf-native-empty">
          Düzenlenebilir metin veya PDF görsel nesnesi bulunamadı. Tarama, vektör çizimi veya özel grafik olabilir.
        </span>}
      </div>}
      {surface && ['native','native-images'].includes(editMode) && !thumbnail && unifiedNative?.objects &&
        <div className="pdf-unified-native-layer" data-testid="unified-native-layer">
          {[...(unifiedNative.objects.objects || [])]
            .filter(obj => editMode !== 'native-images' || obj.type === 'image')
            .sort((a, b) => (unifiedNative.imagesFirst ? (a.type === 'image' ? 1 : -1) : (a.type === 'text' ? 1 : -1)) -
                           (unifiedNative.imagesFirst ? (b.type === 'image' ? 1 : -1) : (b.type === 'text' ? 1 : -1)))
            .map(obj => {
              const active = unifiedNative.selected?.id === obj.id;
              const region = active && unifiedNative.draft ? {...obj.bbox, ...unifiedNative.draft} : obj.bbox;
              const sx = surface.width / (unifiedNative.objects.width || bounds.width);
              const sy = surface.height / (unifiedNative.objects.height || bounds.height);
              return <div key={obj.id} role="button" tabIndex={0}
                className={`pdf-unified-native-hit ${active ? 'active' : ''} ${obj.type === 'image' ? 'image' : 'text'}`}
                title={obj.warning || `${obj.type === 'image' ? 'Görseli' : 'Metni'} seç / taşı`}
                aria-label={`${obj.type === 'image' ? 'Görsel' : 'Metin'}: ${obj.text || obj.id}`}
                style={{left: region.x * sx, top: region.y * sy, width: region.width * sx, height: region.height * sy}}
                onKeyDown={event => { if (event.key === 'Enter') unifiedNative.choose(obj); }}
                onPointerDown={event => {
                  if (event.button !== 0) return;
                  event.preventDefault(); event.stopPropagation();
                  unifiedNative.choose(obj);
                  nativeDragRef.current = {id: obj.id, pointerId: event.pointerId,
                    startX: event.clientX, startY: event.clientY, bbox: obj.bbox,
                    scaleX:sx, scaleY:sy};
                  event.currentTarget.setPointerCapture?.(event.pointerId);
                }}
                onPointerMove={event => {
                  const p = nativeDragRef.current;
                  if (!p || p.id !== obj.id || p.pointerId !== event.pointerId) return;
                  const x = Math.max(0, Math.min((unifiedNative.objects.width || bounds.width) - p.bbox.width,
                    p.bbox.x + (event.clientX - p.startX)/p.scaleX));
                  const y = Math.max(0, Math.min((unifiedNative.objects.height || bounds.height) - p.bbox.height,
                    p.bbox.y + (event.clientY - p.startY)/p.scaleY));
                  unifiedNative.setDraft(previous => ({...previous, x, y}));
                }}
                onPointerUp={() => {nativeDragRef.current = null;}}
                onPointerCancel={() => {nativeDragRef.current = null;}}>
                {active && <span className="pdf-unified-native-label">⠿ {obj.type === 'image' ? 'Görsel' : 'Metin'}</span>}
              </div>;
            })}
        </div>}
      {loading && <Center className="canvas-loading"><Loader size="sm" color="indigo" /></Center>}
      {error && <Center className="canvas-loading"><Text c="red" size="xs">{error}</Text></Center>}
    </div>
  );
}
